import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface PatientHit {
  id:          string;
  name:        string;
  patient_uid: string;
  gender:      string;
  age_years?:  number | null;
  mobile?:     string | null;
  dob?:        string | null;
}

/**
 * Debounced patient search hook.
 *
 * Fires GET /patients?search=<q>&limit=10 only after the user
 * stops typing for `debounceMs` ms and the query is at least
 * `minChars` long.  Results are the decoded `PatientHit[]` from
 * the `data` array in the response envelope.
 *
 * Usage:
 *   const { query, setQuery, results, isSearching, clear } = usePatientSearch();
 */
export function usePatientSearch(debounceMs = 300, minChars = 2) {
  const [rawQuery,  setRawQuery]  = useState('');
  const [debounced, setDebounced] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function setQuery(q: string) {
    setRawQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    // Drop below threshold immediately — no need to wait for debounce
    if (q.length < minChars) {
      setDebounced('');
      return;
    }
    timerRef.current = setTimeout(() => setDebounced(q), debounceMs);
  }

  // Cleanup timer on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const enabled = debounced.length >= minChars;

  const { data, isFetching } = useQuery<PatientHit[]>({
    queryKey:  ['patient-search', debounced],
    queryFn:   () =>
      api.get('/patients', { params: { search: debounced, limit: 10 } })
         .then(r => (r.data.data ?? []) as PatientHit[]),
    enabled,
    staleTime: 30_000,
  });

  function clear() {
    setRawQuery('');
    setDebounced('');
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  return {
    query:       rawQuery,
    setQuery,
    clear,
    /** Non-empty only when debounced query meets minChars threshold */
    results:     enabled ? (data ?? []) : ([] as PatientHit[]),
    isSearching: enabled && isFetching,
  };
}
